import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKnowledgeBase, deleteDocument } from '../hooks/useKnowledgeBase';
import Button from '@/common/ui/components/Button';

export default function KnowledgeBaseList() {
  const navigate = useNavigate();
  const { documents, loading, error, refetch } = useKnowledgeBase();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const categories = ['all', ...Array.from(new Set(documents.map(doc => doc.category)))];

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || doc.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleDelete = async (documentId: string, title: string) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingId(documentId);
    const result = await deleteDocument(documentId);

    if (result.success) {
      refetch();
    } else {
      alert(`Failed to delete document: ${result.error}`);
    }
    setDeletingId(null);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Operations': 'bg-blue-100 text-blue-800',
      'Technical': 'bg-purple-100 text-purple-800',
      'Legal': 'bg-red-100 text-red-800',
      'Safety': 'bg-orange-100 text-orange-800',
      'Process': 'bg-green-100 text-green-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading knowledge base...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-title-lg font-inter text-text-primary">Knowledge Base</h1>
              <p className="text-supporting text-text-secondary font-inter">
                Operational documentation and protocols
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="ghost"
                size="md"
              >
                ← Back to Dashboard
              </Button>
              <Button
                onClick={() => navigate('/admin/knowledge-base/new')}
                variant="primary"
                size="md"
              >
                + New Document
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-trust-blue focus:border-transparent"
              />
            </div>
            <div className="sm:w-48">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-trust-blue focus:border-transparent"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Documents Grid */}
        {filteredDocuments.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-title-md text-gray-900 mb-2">No Documents Found</h3>
            <p className="text-supporting text-text-secondary mb-4">
              {searchQuery || categoryFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first knowledge base document'}
            </p>
            <Button onClick={() => navigate('/admin/knowledge-base/new')} variant="primary">
              + Create First Document
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(doc.category)}`}>
                      {doc.category}
                    </span>
                  </div>

                  <h3
                    onClick={() => navigate(`/admin/knowledge-base/${doc.id}`)}
                    className="text-title-md text-gray-900 mb-2 font-medium cursor-pointer hover:text-trust-blue transition-colors"
                  >
                    {doc.title}
                  </h3>

                  <p className="text-supporting text-text-secondary line-clamp-3 mb-4">
                    {doc.content.substring(0, 150)}...
                  </p>

                  <div className="text-caption text-gray-500 mb-4">
                    Updated {formatDate(doc.updatedAt)}
                  </div>

                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => navigate(`/admin/knowledge-base/${doc.id}`)}
                      className="flex-1 px-3 py-2 text-sm font-medium text-trust-blue hover:bg-blue-50 rounded transition-colors"
                    >
                      View
                    </button>
                    <button
                      onClick={() => navigate(`/admin/knowledge-base/${doc.id}/edit`)}
                      className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(doc.id, doc.title)}
                      disabled={deletingId === doc.id}
                      className="flex-1 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                    >
                      {deletingId === doc.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
